/**
 * Deemed income & deprivation — the two rules that make a client's means-test position larger than
 * what they literally hold, as a pure function.
 *
 *   - DEEMING: Centrelink does not ask what a client's financial assets actually earn. It assumes a
 *     return — 1.25% up to the situation's threshold, 3.25% above it — and assesses that instead. A
 *     client with $500,000 in a zero-interest account is assessed on deemed income all the same, and a
 *     client whose shares beat the rates keeps the difference untested.
 *   - DEPRIVATION: a gift above the free areas doesn't leave the balance sheet for five years. What is
 *     "deprived" keeps counting BOTH ways — as an asset for the assets test, and as a financial asset
 *     that is then deemed — which is why the two rules are struck together here rather than apart: the
 *     deeming base includes the deprived amount. A granny-flat arrangement whose entry contribution
 *     exceeded the reasonableness cap is deprivation of exactly the same kind, and is folded in.
 *
 * What this does NOT do is decide which assets are financial in the first place, or apply any threshold
 * to the income it produces. The former is upstream in `assessableMeans.ts`; the latter is the income
 * test in `agePension.ts`. It takes the base as given and hands back a larger one.
 *
 * Extracted from `calculateFinancials`'s "--- 2. Deemed Income from Financial Assets ---" block, which
 * still calls it — through the injectable {@link DeemedIncomeAssessor} seam, so the projection engine
 * can hand in the assessor owned by its deemedIncomePlugin. See that plugin for why the code lives here
 * rather than in it.
 */

import { DEEMING_RATE_LOWER, DEEMING_RATE_UPPER } from '../../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { getGrannyFlatDeprivationFromFactFind } from '../../../../../shared/src/calculation/grannyFlatArrangement.ts';
import { calculateDeprivedAssets, getAustralianFYEndingYearContainingDate } from '../../../../../shared/src/calculation/giftingDeprivation.ts';
import type { FactFind, FactFindGiftsHistory } from '../../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions } from '../../../../../shared/src/calculation/strategyOptionsTypes.ts';

/**
 * The two-tier deeming calculation on its own: the lower rate up to `threshold`, the upper rate on
 * everything above it. Exported because it is the whole of the deeming rule and worth being able to
 * state — and check — in one line.
 */
export function deemIncomeOn(financialAssets: number, threshold: number): number {
    if (financialAssets <= threshold) return financialAssets * DEEMING_RATE_LOWER;
    return threshold * DEEMING_RATE_LOWER + (financialAssets - threshold) * DEEMING_RATE_UPPER;
}

/** The position deeming and deprivation are struck against. */
export interface DeemedIncomeInput {
    /** The rate tables for the year being assessed — read for `deemingThresholds` and, for the granny-flat cap, `pensionRates.couple`. */
    rates: any;
    /** `'couple'` or `'single'` — the key the deeming threshold is indexed by. */
    relationshipStatus: string;
    /** Read for `giftsHistory` and the granny-flat arrangement. */
    factFind: FactFind;
    /** Read for the gifts a STRATEGY proposes (downsize, other-strategies, investment-property sale). */
    strategyOptions: StrategyOptions;
    /**
     * The date the five-year deprivation window and the gifting free areas are struck against — "today"
     * for a standalone call, and the projection anchor for a projected year, so a gift ages out of the
     * assessment at the right point in a projection rather than never.
     */
    asOfDate: Date;
    /** Assessable assets BEFORE deprivation, as `assessableMeans.ts` assessed them. */
    assessableAssets: number;
    /** The deeming base BEFORE deprivation, likewise. */
    financialAssetsForDeeming: number;
}

/** What deeming and deprivation added to the position. */
export interface DeemedIncomeAssessment {
    /** The assumed return on {@link financialAssetsForDeeming} — the largest income component for most retirees. */
    deemedIncome: number;
    /** The balance the lower rate applied up to. Reported in the Age Pension breakdown's deeming detail. */
    deemingThreshold: number;
    /** Gifts (and granny-flat over-contribution) above the free areas, still counted for five years. */
    deprivedAsset: number;
    /** {@link DeemedIncomeInput.assessableAssets} with {@link deprivedAsset} included. */
    assessableAssets: number;
    /** {@link DeemedIncomeInput.financialAssetsForDeeming} with {@link deprivedAsset} included — the base {@link deemedIncome} was struck on. */
    financialAssetsForDeeming: number;
}

/**
 * Assesses deemed income and deprivation. Injected into `calculateFinancials` so a caller can own the
 * rules — see {@link calculateDeemedIncome}, the default, and the projection engine's
 * deemedIncomePlugin.
 */
export type DeemedIncomeAssessor = (input: DeemedIncomeInput) => DeemedIncomeAssessment;

/**
 * Adds this year's deprivation to both totals, then deems the enlarged financial-asset base.
 *
 * The order is the point: deprivation first, deeming second, so a gift is assessed as though the client
 * still held the money AND as though that money were still earning. Returning the enlarged totals rather
 * than just the deprived amount keeps that single fact — deprived assets are deemed — in one place.
 */
export const calculateDeemedIncome: DeemedIncomeAssessor = (input: DeemedIncomeInput): DeemedIncomeAssessment => {
    const { rates, relationshipStatus, factFind, strategyOptions, asOfDate } = input;

    const deemingThreshold = (rates.deemingThresholds as any)[relationshipStatus];

    // A granny-flat entry contribution above the reasonableness cap is deprivation dated to the
    // financial year the arrangement was established, not to today — so it ages out five years from
    // then. Passed to the gifting engine as an extra gift in that year.
    let grannyFlatDeprivationByFy: Record<number, number> | null = null;
    {
        const gfDep = getGrannyFlatDeprivationFromFactFind(factFind, rates.pensionRates.couple);
        if (gfDep && gfDep.deprivation > 0) {
            grannyFlatDeprivationByFy = { [gfDep.establishmentFyEnd]: gfDep.deprivation };
        }
    }

    const deprivedAsset = calculateDeprivedAssets(factFind.giftsHistory || {}, strategyOptions, asOfDate, grannyFlatDeprivationByFy);
    const assessableAssets = input.assessableAssets + deprivedAsset;
    const financialAssetsForDeeming = input.financialAssetsForDeeming + deprivedAsset;

    return {
        deemedIncome: deemIncomeOn(financialAssetsForDeeming, deemingThreshold),
        deemingThreshold,
        deprivedAsset,
        assessableAssets,
        financialAssetsForDeeming,
    };
};
